import CallButton from '@acorn-portal/shared/src/components/buttons/CallButton';
import IncreaseMileageButton from '@acorn-portal/shared/src/components/buttons/IncreaseMileageButton';
import PayNowAlerts from '@acorn-portal/shared/src/components/content/pay-now-alerts';
import RenewalOptInForm from '@acorn-portal/shared/src/components/forms/renewal-opt-in-form';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import PolicyPaymentScheduleCTA from '@acorn-portal/shared/src/components/paymentDetails/PolicyPaymentScheduleCTA';
import PolicyDocuments from '@acorn-portal/shared/src/components/policy/documents/policy-documents';
import PolicyBreakdown from '@acorn-portal/shared/src/components/policy/policy-breakdown';
import RenewalAlerts from '@acorn-portal/shared/src/components/policy/renewal-alerts';
import PolicyStatusField from '@acorn-portal/shared/src/components/status/policyStatusField';
import { DriverScoreCard } from '@acorn-portal/shared/src/components/telematics/driver-score';
import { MileageAlerts, MileageTracker } from '@acorn-portal/shared/src/components/telematics/mileage';
import { RecentTripsCard } from '@acorn-portal/shared/src/components/telematics/trip';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useTelematicsFeature } from '@acorn-portal/shared/src/hooks/useTelematicsFeature';
import { useDriverScoreVisibility } from '@acorn-portal/shared/src/hooks/useDriverScoreVisibility';
import usePayNowDetails from '@acorn-portal/shared/src/models/paynow-model';
import { usePolicy } from '@acorn-portal/shared/src/models/policy-model';
import AddOnPhoneNumbers from '@acorn-portal/shared/src/pages/policy/components/add-on-phone-numbers';
import AutoRenewalIneligibilityContent from '@acorn-portal/shared/src/pages/policy/components/auto-renew-eligibility-content';
import DashboardCard from '@acorn-portal/shared/src/pages/policy/components/dashboard-card';
import MtaJourneyAccess from '@acorn-portal/shared/src/pages/policy/components/journey-access/mta/mta-journey-access';
import { useMtaAccessConfig } from '@acorn-portal/shared/src/pages/policy/hooks/journey-access/mta/use-mta-access-config';
import { getAutoRenewalEligibility } from '@acorn-portal/shared/src/pages/policy/utils/get-auto-renewal-eligibility';
import { PolicyStatus } from '@acorn-portal/shared/src/services/api';
import {
	clearMidTermAdjustmentDetails,
	setPolicyReference,
	setPolicyToken,
	setStartDate,
} from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { resetApiMTA } from '@acorn-portal/shared/src/store/midTermAdjustmentAPI.sclice';
import { clearMidTermAdjustmentResponseDetails } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { clearRenewalState } from '@acorn-portal/shared/src/store/renewal.slice';
import { resetRenewalChanges } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import { checkPolicyExists } from '@acorn-portal/shared/src/store/telematicsEnabledPolicySlice';
import { getInitialMTAStartDate, isProtectYD } from '@acorn-portal/shared/src/utils';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, CardContent, Divider, Fade, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { canOptOut } from './utils';
import { format, parseISO } from 'date-fns';
import AutoRenewedBlocked from '@acorn-portal/shared/src/components/forms/auto-renew-blocked';

const TelematicsPolicyDetails = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { policyToken } = useParams<{ policyToken: string }>();
	const [searchParams] = useSearchParams();
	const { loading, fullPolicy } = usePolicy(policyToken as string);
	const policyNumber = decodeURIComponent(searchParams.get('policyNumber') ?? fullPolicy.policyNumber);
	const { payNowDetails, loadingPayNow } = usePayNowDetails(policyNumber);
	const claimsPhoneNumber = getPhoneNumber(fullPolicy.product, 'CLAIMS');
	const contactPhoneNumber = getPhoneNumber(fullPolicy.product, 'CONTACT');
	const renewContactNumber = getPhoneNumber(fullPolicy.product, 'AUTO_RENEW_CONTACT');
	// Get mileage summary for alerts
	const { summary: mileageSummary } = useAppSelector((state) => state.telematicsDriverMileage);

	// ** Journey Access **
	// Overarching
	const activePolicy = fullPolicy.isActive;
	// MTA access
	const mtaAccessConfig = useMtaAccessConfig();
	// Payment schedule access
	const canAccessPaymentSchedule =
		activePolicy && fullPolicy.isDirectDebitPaymentMethod && !fullPolicy.isShortTermPolicy;
	// Renewal logic
	const isEndOfProduct = fullPolicy.policyStatus === PolicyStatus.EndOfProduct;
	const policyCanOptOut = canOptOut(fullPolicy.policyStatus);
	const isProtectYoungDriver = isProtectYD(fullPolicy.product, fullPolicy.scheme ?? '');
	const showRenewalContent = (policyCanOptOut && !fullPolicy.renewalDeclined) || isEndOfProduct;
	const isActiveProtectYoungDriver = isProtectYoungDriver && !isEndOfProduct;
	const autoRenewalEligibility = getAutoRenewalEligibility(isActiveProtectYoungDriver, isEndOfProduct);
	// Telematics logic
	const { policyExists } = useAppSelector((state) => state.telematicsEnabledPolicy);
	const isTelematicsPolicyEnabled = policyExists?.exists ?? false;
	// Telematics feature flag - controls whether telematics API calls are made
	const isTelematicsFeatureEnabled = useTelematicsFeature();
	// Feature flag must be on AND the mileage exists API must report the policy as TREV-enabled.
	// Product type alone is not enough — Carrot/telematics products can still return exists: false.
	const isTelematicsEnabled = isTelematicsFeatureEnabled && isTelematicsPolicyEnabled;
	// Driver Score Visibility feature flag
	const showDriverScore = useDriverScoreVisibility();
	const formattedEndDate = format(parseISO(fullPolicy.endDate), "do 'of' MMMM yyyy");
	const isAutoRenewalChangeBlocked = fullPolicy.isAutoRenewalChangeBlocked;

	useEffect(() => {
		dispatch(clearRenewalState());
		dispatch(resetRenewalChanges());
		dispatch(clearMidTermAdjustmentDetails());
		dispatch(clearMidTermAdjustmentResponseDetails());
		dispatch(resetApiMTA());
		dispatch(setPolicyReference(fullPolicy.policyNumber));
		dispatch(setPolicyToken(fullPolicy.policyToken));
		dispatch(setStartDate(getInitialMTAStartDate()));
		// Check if policy is telematics enabled (only if feature flag is on)
		if (isTelematicsFeatureEnabled && fullPolicy.policyNumber) {
			dispatch(checkPolicyExists({ policyReference: fullPolicy.policyNumber }));
		}
	}, [dispatch, dispatch, fullPolicy.policyNumber, fullPolicy.policyToken, isTelematicsFeatureEnabled]);

	// Rendered in the left column on mobile and the right column on desktop
	const mileageCard = activePolicy && (
		<DashboardCard>
			<CardContent>
				<MileageTracker hideTopUpButton />
				<IncreaseMileageButton payNowDetails={payNowDetails} />
			</CardContent>
		</DashboardCard>
	);

	return (
		<FullPageLoadingSplash message='Fetching Policy Details...' loading={loading || loadingPayNow}>
			<Fade in>
				<Box>
					<Box>
						<Grid container spacing={2} sx={{ mb: { xs: 'auto', md: '1em' } }}>
							<Grid size={{ xs: 12 }}>
								<RenewalAlerts policy={fullPolicy} />
								<PayNowAlerts payNowDetails={payNowDetails.details} />
								{isTelematicsEnabled && activePolicy && <MileageAlerts mileageSummary={mileageSummary} />}
							</Grid>
							<Grid size={{ xs: 12, md: 7 }} display='flex' flexDirection='column' gap={2}>
								{/* Driver Score card is omitted when the visibility flag is off so a replacement card can take this slot */}
								{isTelematicsEnabled && showDriverScore && (
									<DashboardCard>
										<CardContent>
											<DriverScoreCard policyNumber={fullPolicy.policyNumber} />
										</CardContent>
									</DashboardCard>
								)}
								{/* Recent Trips Card - latest trips with a map of the most recent one */}
								{isTelematicsEnabled && activePolicy && (
									<DashboardCard>
										<CardContent>
											<RecentTripsCard policyNumber={fullPolicy.policyNumber} showDriverScore={showDriverScore} />
										</CardContent>
									</DashboardCard>
								)}
								{isMobile && mileageCard}
								{/* Policy Details Card */}
								<DashboardCard>
									<CardContent>
										<Typography
											variant='dashboard-card-heading'
											component='h1'
										>{`Policy Details | ${fullPolicy.policyNumber}`}</Typography>
										<PolicyStatusField policy={fullPolicy} />
										<PolicyBreakdown policy={fullPolicy} />
										{/* MTA Journey Access */}
										{activePolicy && (
											<>
												<Divider />
												<Box sx={{ m: '1rem', mb: 0 }}>
													<Typography component='h3' sx={{ fontWeight: 700 }}>
														Make a change
													</Typography>
													<Typography sx={{ my: '1rem' }}>
														Make a change to your policy, vehicle or personal information.
													</Typography>
													<MtaJourneyAccess
														handleClick={() => navigate(ROUTE_PATHS.makeAChangeHome)}
														permitAccess={mtaAccessConfig.permitted}
														showAccessMessage={mtaAccessConfig.notification.show}
														reasonForBlockedAccess={mtaAccessConfig.notification.reasonBlocked}
													/>
												</Box>
											</>
										)}
									</CardContent>
								</DashboardCard>
							</Grid>
							<Grid size={{ xs: 12, md: 5 }} display='flex' flexDirection='column' gap={2} pb={2}>
								{!isMobile && mileageCard}
								<DashboardCard>
									<CardContent>
										<Typography variant='dashboard-card-heading' sx={{ mb: '1rem' }}>
											Policy Documents
										</Typography>
										<PolicyDocuments />
									</CardContent>
								</DashboardCard>
								<DashboardCard>
									<CardContent>
										<Typography variant='dashboard-card-heading' sx={{ mb: '1rem' }}>
											Claims & Breakdown
										</Typography>
										<CallButton buttonLabel={'Claims: ' + claimsPhoneNumber} phoneNumber={claimsPhoneNumber} />
										<AddOnPhoneNumbers addOns={fullPolicy.addOns} product={fullPolicy.product} />
									</CardContent>
								</DashboardCard>
								{canAccessPaymentSchedule && <PolicyPaymentScheduleCTA />}
								{showRenewalContent && (
									<DashboardCard>
										<CardContent>
											<Typography variant='dashboard-card-heading'>
												{fullPolicy.isShortTermPolicy ? 'Rolling Subscription' : 'Your Renewal'}
											</Typography>
											{fullPolicy.autoRenewal && autoRenewalEligibility.status === 'eligible' && (
												<Typography sx={{ mt: 2 }}>
													Your policy is set to auto renew on the {formattedEndDate}.
												</Typography>
											)}
											{autoRenewalEligibility.status === 'eligible' ? (
												isAutoRenewalChangeBlocked ? (
													<AutoRenewedBlocked
														isShortTermPolicy={fullPolicy.isShortTermPolicy}
														contactPhoneNumber={renewContactNumber}
														autoRenewalOptIn={fullPolicy.autoRenewal}
														expiryDate={formattedEndDate}
													/>
												) : (
													<RenewalOptInForm
														isShortTermPolicy={fullPolicy.isShortTermPolicy}
														policyToken={fullPolicy.policyToken}
														autoRenewal={fullPolicy.autoRenewal}
														expiryDate={fullPolicy.endDate}
													/>
												)
											) : (
												<AutoRenewalIneligibilityContent
													reason={autoRenewalEligibility.reason}
													contactPhoneNumber={contactPhoneNumber}
												/>
											)}
										</CardContent>
									</DashboardCard>
								)}
							</Grid>
						</Grid>
					</Box>
				</Box>
			</Fade>
		</FullPageLoadingSplash>
	);
};

export default TelematicsPolicyDetails;
